import { useState, useEffect, useCallback } from 'react';
import { api, ApiError } from '../lib/api';

/* ============================================================================
   THE PRIVACY NOTICE ADMIN

   ⚠ THE SCHOOL PASTES THEIR ADVOCATE'S WORDING IN HERE.

   No wording ships with the product, deliberately. A privacy notice written by a
   software vendor, and then RELIED ON by a school, is worse than none — because
   they would be relying on it.

   The software's job is narrower and more useful: make sure that whatever the
   advocate writes, the school can PROVE who accepted it, when, and which version.
   ========================================================================== */

interface Notice {
  id: string; version: string; body: string;
  isPublished: boolean; publishedAt: string | null;
}

export function PrivacyNoticePage() {
  const [rows, setRows] = useState<Notice[] | null>(null);
  const [editing, setEditing] = useState<Notice | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api.get<Notice[]>('/api/privacy/notices').then(setRows).catch(() => setRows([]));
  }, []);
  useEffect(load, [load]);

  const live = rows?.find((n) => n.isPublished);

  return (
    <div>
      <div className="page-head">
        <div>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Legal</div>
          <h1 className="h-display" style={{ fontSize: 28 }}>Privacy notice</h1>
        </div>
        <div className="page-head-actions">
          <button className="btn btn-accent" onClick={() => setCreating(true)}>+ New version</button>
        </div>
      </div>

      {/* ⚠ Nothing published = nobody is being asked to consent to anything. Say so, loudly. */}
      {rows && !live && (
        <div className="card" style={{ padding: 16, marginBottom: 18, borderColor: 'var(--brand)' }}>
          <strong style={{ fontSize: 14.5, color: 'var(--brand)' }}>
            ⚠ No privacy notice is published
          </strong>
          <p style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 4, maxWidth: '62ch' }}>
            Nobody is being asked to consent to anything. Under the DPDP Act you need verifiable
            parental consent before processing a child's data.{' '}
            <strong>Paste the wording your legal counsel gave you</strong>, then publish it — every
            user will be asked to read and accept it before they can use the app.
          </p>
        </div>
      )}

      {error && (
        <div className="pill pill-danger" style={{ padding: '9px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>
          {error}
        </div>
      )}

      {!rows ? (
        <div className="card" style={{ padding: 20 }}><span className="sk" style={{ width: '50%' }} /></div>
      ) : rows.length === 0 ? (
        <div className="empty">
          <h3>No notice yet</h3>
          <p>Create the first version, using the text your advocate provided.</p>
          <button className="btn btn-accent" onClick={() => setCreating(true)}>+ New version</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 720 }}>
          {rows.map((n) => (
            <div key={n.id} className="card" style={{
              padding: 16,
              borderColor: n.isPublished ? 'var(--ok-ink, #1e7d4f)' : undefined,
            }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <strong style={{ fontSize: 15, fontFamily: 'var(--font-display)' }}>
                  Version {n.version}
                </strong>

                {n.isPublished
                  ? <span className="pill pill-ok" style={{ fontSize: 11 }}>LIVE — everyone must accept this</span>
                  : <span className="pill pill-muted" style={{ fontSize: 11 }}>draft</span>}

                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                  {!n.isPublished ? (
                    <>
                      <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }}
                        onClick={() => setEditing(n)}>
                        Edit
                      </button>
                      <button className="btn btn-accent" style={{ padding: '4px 10px', fontSize: 12 }}
                        onClick={async () => {
                          if (!window.confirm(
                            'Publish this?\n\nEvery user — staff and parents — will be blocked from the app until they read and accept it.'
                          )) return;
                          try {
                            await api.post(`/api/privacy/notices/${n.id}/publish`, {});
                            load();
                          } catch (e) {
                            setError(e instanceof ApiError ? e.message : 'Could not publish.');
                          }
                        }}>
                        Publish
                      </button>
                    </>
                  ) : (
                    <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }}
                      onClick={() => setEditing(n)}>
                      View
                    </button>
                  )}
                </div>
              </div>

              <p style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 6 }}>
                {n.body.slice(0, 140)}{n.body.length > 140 ? '…' : ''}
              </p>
            </div>
          ))}
        </div>
      )}

      {(creating || editing) && (
        <NoticeEditor
          notice={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onDone={() => { setCreating(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function NoticeEditor({ notice, onClose, onDone }: {
  notice: Notice | null; onClose: () => void; onDone: () => void;
}) {
  const [version, setVersion] = useState(notice?.version ?? '');
  const [body, setBody] = useState(notice?.body ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A published notice is EVIDENCE. It must not move.
  const readOnly = notice?.isPublished ?? false;

  const save = async () => {
    if (!version.trim() || !body.trim()) return;
    setBusy(true); setError(null);
    try {
      if (notice) await api.put(`/api/privacy/notices/${notice.id}`, { version, body });
      else await api.post('/api/privacy/notices', { version: version.trim(), body: body.trim() });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save.');
    } finally { setBusy(false); }
  };

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <h2 className="h-display" style={{ fontSize: 20, marginBottom: 4 }}>
          {readOnly ? 'Published notice' : notice ? 'Edit draft' : 'New privacy notice'}
        </h2>

        {readOnly ? (
          <p style={{ color: 'var(--ink-2)', fontSize: 13, marginBottom: 14 }}>
            This is live and <strong>cannot be edited</strong> — people have already accepted this exact
            text, and changing it would destroy your own evidence. Create a new version instead.
          </p>
        ) : (
          <p style={{ color: 'var(--ink-2)', fontSize: 13, marginBottom: 14 }}>
            Paste the wording your <strong>legal counsel</strong> gave you. Don't write it yourself —
            a notice you rely on that turns out to be defective is worse than no notice at all.
          </p>
        )}

        {error && (
          <div className="pill pill-danger" style={{ padding: '9px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <label className="label">Version</label>
            <input className="input mono" placeholder="v1.0" value={version} disabled={readOnly}
              onChange={(e) => setVersion(e.target.value)} style={{ maxWidth: 140 }} />
          </div>
          <div>
            <label className="label">The notice</label>
            <textarea className="input" rows={16} value={body} disabled={readOnly}
              placeholder="Paste your advocate's wording here…"
              onChange={(e) => setBody(e.target.value)}
              style={{ fontFamily: 'inherit', lineHeight: 1.6 }} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button className="btn" onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</button>
          {!readOnly && (
            <button className="btn btn-accent" disabled={busy || !version.trim() || !body.trim()}
              onClick={save} style={{ marginLeft: 'auto' }}>
              {busy ? 'Saving…' : 'Save draft'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
